/**
 * FE-side label maps for the templates surface.
 *
 * The BE intentionally does NOT return `documentTypeLabel` or
 * namespace-label strings (FE-spec v1.1) — the FE owns presentation copy,
 * BE owns enum values only. Two maps live here so consumers (library
 * section headers, drawer metadata strip, merge-fields-by-namespace
 * grouping) share a single source of truth.
 */

import {
  ESTATE_PLANNING_DOCUMENT_TYPES,
  QLD_COURT_FORM_DOCUMENT_TYPES
} from './types';
import type { DocumentType, MergeFieldNamespace } from './types';

export const DOCUMENT_TYPE_LABELS: Record<DocumentType, string> = {
  declaration: 'Beneficiary Declaration',
  'costs-agreement': 'Costs Agreement',
  'enquiry-costs-agreement': 'Costs Agreement (New Enquiry)',
  'engagement-letter': 'Engagement Letter',
  'da-cover-letter': 'Distribution Advice Cover Letter',
  'distribution-advice': 'Distribution Advice',
  'distribution-statement': 'Distribution Statement',
  'institution-letter': 'Institution Letter Letterhead',
  will: 'Will',
  'vic-epoa': 'VIC Enduring Power of Attorney',
  'mtdm-appointment': 'Medical Treatment Decision Maker Appointment',
  'ep-cover-letter': 'Estate Planning Cover Letter',
  'letter-of-advice': 'Letter of Advice',
  'prior-documents-authority':
    'Authority to Retrieve or Destroy Prior Documents',
  'executor-remuneration-consent': 'Consent to Executor Remuneration',
  'will-schedule': 'Will Schedule',
  codicil: 'Codicil',
  'mutual-wills-agreement': 'Mutual Wills Agreement',
  'statement-of-wishes': 'Statement of Wishes',
  'letter-of-reasons': 'Letter of Reasons',
  'affidavit-of-testamentary-explanation':
    'Affidavit of Testamentary Explanation',
  'smsf-binding-nomination': 'SMSF Binding Death Benefit Nomination',
  'corporate-power-of-attorney': 'Corporate Power of Attorney',
  'vic-advance-care-directive': 'VIC Advance Care Directive',
  'qld-advance-health-directive': 'QLD Advance Health Directive',
  'ep-options-letter': 'Estate Planning Options Letter',
  'will-explainer-letter': 'Letter to Keep with the Will',
  'clause-bank': 'Clause Bank',
  'qld-form-101': 'QLD Form 101, Application for Probate',
  'qld-form-104': 'QLD Form 104, Affidavit of Publication and Service',
  'qld-form-105': 'QLD Form 105, Affidavit Supporting Probate Application',
  custom: 'Your Documents'
};

/** Enum natural order — drives section ordering on the library landing.
 *  `custom` sorts last: Vale's standards read as the reference set, the firm's
 *  own precedents as the addition below them. */
export const DOCUMENT_TYPE_ORDER: DocumentType[] = [
  'declaration',
  'costs-agreement',
  // Directly after the matter-stage one: a lawyer looking for either is looking
  // for "the costs agreement", and adjacency is what makes the labels do their job.
  'enquiry-costs-agreement',
  'engagement-letter',
  'da-cover-letter',
  'distribution-advice',
  // The Statement sits next to the Advice: they are the two halves of one
  // distribution (the authorisation and the account), and a firm looks for them
  // together.
  'distribution-statement',
  // Last of the Vale-defined types, before the firm's own bucket: it is
  // stationery rather than a document, so it reads oddly among the precedents
  // and belongs at the end of that group.
  'institution-letter',
  // The estate-planning set, in the package's own signing order ([EPLAN-30]).
  ...ESTATE_PLANNING_DOCUMENT_TYPES,
  // The Queensland court forms, in form order (QCF-2).
  ...QLD_COURT_FORM_DOCUMENT_TYPES,
  'custom'
];

/**
 * Whether a document type has an `isDefault` concept — mirrors the BE's
 * `DocumentTypeDefinition.supportsDefault`.
 *
 * A Vale-defined type does: generation resolves "the firm's default declaration"
 * when no template is named. The custom bucket does **not** — its members are
 * unrelated documents in one flat namespace, so "the firm's default custom
 * precedent" is meaningless. The BE rejects a default-flip on it
 * (`DOCUMENT_TYPE_NO_DEFAULT`), so the FE must not offer the action.
 */
export const DOCUMENT_TYPE_SUPPORTS_DEFAULT: Record<DocumentType, boolean> = {
  declaration: true,
  'costs-agreement': true,
  'enquiry-costs-agreement': true,
  'engagement-letter': true,
  'da-cover-letter': true,
  'distribution-advice': true,
  'distribution-statement': true,
  // One letterhead per firm, so the default concept applies.
  'institution-letter': true,
  ...(Object.fromEntries(
    ESTATE_PLANNING_DOCUMENT_TYPES.map((type) => [type, true])
  ) as Record<(typeof ESTATE_PLANNING_DOCUMENT_TYPES)[number], boolean>),
  'qld-form-101': true,
  'qld-form-104': true,
  'qld-form-105': true,
  custom: false
};

export function supportsDefault(type: DocumentType): boolean {
  return DOCUMENT_TYPE_SUPPORTS_DEFAULT[type] ?? true;
}

export const MERGE_FIELD_NAMESPACE_LABELS: Record<MergeFieldNamespace, string> =
  {
    recipient: 'Recipient',
    deceased: 'Deceased',
    lpr: 'Legal personal representative',
    lawFirm: 'Law firm',
    signatory: 'Signatory',
    lawyer: 'Signatory',
    office: 'Office',
    matter: 'Matter',
    estate: 'Estate',
    lead: 'Enquiry',
    testator: 'Client',
    partner: 'Client’s partner',
    family: 'Family',
    plan: 'Letter of advice',
    court: 'Court',
    exhibit: 'Exhibits',
    system: 'System',
    other: 'Other'
  };

export function documentTypeLabel(type: DocumentType): string {
  return DOCUMENT_TYPE_LABELS[type] ?? type;
}

export function namespaceLabel(ns: MergeFieldNamespace): string {
  return MERGE_FIELD_NAMESPACE_LABELS[ns] ?? ns;
}

/** Wrap a bare field `name` (`beneficiary.fullName`) in the merge-token
 *  delimiters Vale's engine uses (`<<beneficiary.fullName>>`). */
export function mergeFieldToken(name: string): string {
  return `<<${name}>>`;
}
